import { useContext, useState } from 'react';
import HeaderWrapper from '@/components/ChallengePortfolio/NormalView/Challenge/ChallengeView/ChallengeHeader/HeaderWrapper';
import InnerHeaderWrapper from '@/components/ChallengePortfolio/NormalView/Challenge/ChallengeView/ChallengeHeader/InnerHeaderWrapper';
import ChallengeName from '@/components/ChallengePortfolio/NormalView/Challenge/ChallengeView/ChallengeHeader/ChallengeName';
import TaskContext from '../TaskContext';
import RestrictedToPortfolioMode from '@/components/RestrictedToPortfolioMode';
import PortfolioMode from '@/lib/constants/PortfolioMode.enum';
import TaskNameEditor from './TaskNameEditor';
import CopyTask from './options/CopyTask';
import DeleteTask from './options/DeleteTask';
import TaskLinkSharing from './options/TaskLinkSharing';
import useRandomizedId from '@/lib/utils/useRandomizedId';
import { Divider, IconButton, Tooltip } from '@mui/material';
import MenuWithLazyPersistedMounting from '../../../../../Menu/MenuWithLazyPersistedMounting';
import { SettingsOutlined as OptionsIcon } from '@mui/icons-material';
import EditNameOption from './options/EditNameOption';
import UploadThumbnail from '../../../portfolio-item/PortfolioItemThumbnailHeader/UploadThumbnail';
import { FormattedMessage } from 'react-intl';
import useForceStateUpdate from '@/lib/utils/useForceStateUpdate';
import ModelInfo from '../../../Challenge/ChallengeView/ChallengeHeader/ModelInfo';
import ModelType from '@/lib/constants/challengeModel/ModelType.enum';

const TaskHeader = () => {
  const [task] = useContext(TaskContext);

  const [nameEditing, setNameEditing] = useState(false);

  const optionsButtonId = useRandomizedId('task-options-button');
  const optionsMenuId = useRandomizedId('task-options-menu');

  const [optionsMenuAnchorElement, setOptionsMenuAnchorElement] =
    useState<HTMLButtonElement | null>(null);
  const optionsMenuOpen = optionsMenuAnchorElement !== null;

  const openOptionsMenu = (e: React.MouseEvent<HTMLButtonElement>): void => {
    setOptionsMenuAnchorElement(e.currentTarget);
  };

  const closeOptionsMenu = (): void => {
    setOptionsMenuAnchorElement(null);
  };

  const forceStateUpdate = useForceStateUpdate();

  if (nameEditing) {
    return <TaskNameEditor onClose={() => setNameEditing(false)} />;
  }

  return (
    <HeaderWrapper>
      <InnerHeaderWrapper>
        <ChallengeName>{task.name}</ChallengeName>
        <RestrictedToPortfolioMode mode={PortfolioMode.STUDENT}>
          <Tooltip
            title={
              <FormattedMessage id="portfolio.challenge.options.button_tooltip.as_task" />
            }
          >
            <IconButton
              id={optionsButtonId}
              onClick={openOptionsMenu}
              aria-controls={optionsMenuOpen ? optionsMenuId : undefined}
              aria-expanded={optionsMenuOpen ? 'true' : undefined}
              aria-haspopup
            >
              <OptionsIcon />
            </IconButton>
          </Tooltip>
          <MenuWithLazyPersistedMounting
            id={optionsMenuId}
            labelledById={optionsButtonId}
            anchorEl={optionsMenuAnchorElement}
            open={optionsMenuOpen}
            onClose={closeOptionsMenu}
          >
            <ModelInfo variant={ModelType.TASK} />
            <EditNameOption
              onClick={() => {
                closeOptionsMenu();

                setNameEditing(true);
              }}
            />
            <Divider />
            {task.thumbnail === null && (
              <UploadThumbnail
                variant="add"
                onForceMenuStateUpdate={forceStateUpdate}
                onCloseMenu={closeOptionsMenu}
              />
            )}
            <TaskLinkSharing />
            <Divider />
            <CopyTask />
            <DeleteTask />
          </MenuWithLazyPersistedMounting>
        </RestrictedToPortfolioMode>
      </InnerHeaderWrapper>
    </HeaderWrapper>
  );
};

export default TaskHeader;
